import React, { useEffect, useState, useMemo } from "react";
import { ModalBody } from "@vds/modals";
import { EMAIL_REGEX_C360 } from "../../../constants/ValidationConstants";
import CustomModal from "../../../components/core/CustomModal";
import { CustomSLButton, FlexRow, CustomInput, SLNotification } from "../../../components/core/utility";
import { Loader } from "@vds/loaders";
import isEmpty from "lodash/isEmpty";
import { generate } from "shortid";
import { KeyFobValidateAddress } from "./KeyFobValidateAddress";
import styled from "styled-components";
import { useToSaveKeyFobs } from "../../../services/workticket";
const CustomNotification = styled(SLNotification)`
  & div {
    justify-content: flex-start;
    max-width: 100%;
    & span:first-child {
      flex: 20 1 0%;
    }
  }
`;
const AddKeyFobsModal = ({ open = false, close, selectedKeyFobRow, cspData, keyFobData }) => {
  const [isLoading, setLoader] = useState(false);
  const { isLoading: saveKeyFobsLoading, data, error, saveKeyFobsData } = useToSaveKeyFobs();
  const initialFields = {
    name: { value: "", error: false, errorText: "Enter name", success: false },
    phone: { value: "", error: false, errorText: "Enter phone number", success: false },
    email: { value: "", error: false, errorText: "Enter email", success: false },
    quantity: { value: "", error: false, errorText: "Enter quantity", success: false },
  };
  const [formFields, setFormFields] = useState(initialFields);
  const [validAddress, setValidAddress] = useState({ isNewAddress: false, verified: false });
  const [notification, setNotification] = useState(null);
  const [address, setAddress] = useState({
    verified: false,
    addressByPass: null,
    locationFound: null,
    timezone: "",
    latitude: "",
    longitude: "",
    locusSuggestedAddress: "",
    historicalAddress: { value: "", error: false, errorText: "", success: false },
    address1: { value: "", error: false, errorText: "Enter valid street address", success: false },
    address2: { value: "", error: false, errorText: "Enter address 2", success: false },
    city: { value: "", error: false, errorText: "Enter city", success: false },
    country: { value: null, options: [], error: false, errorText: "Please select country", success: false },
    state: { value: null, options: [], allStateOptions: null, error: false, errorText: "Please select state", success: false },
    zip: { value: "", error: false, errorText: "Enter zip code", success: false },
  });
  const callBackNotify = (content, type, isNotificationWithTimer = false) => {
    setNotification(
      <CustomNotification key={generate()} type={type} title={content} fullBleed={true} onCloseButtonClick={() => setNotification(null)} />
    );
    if (isNotificationWithTimer) {
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    }
  };
  useEffect(() => {
    if (!saveKeyFobsLoading && !isEmpty(data)) {
      if (isEmpty(error)) {
        callBackNotify("Assigned to address successfullly saved", "success");
        setTimeout(() => {
          setFormFields(initialFields);
          close(false, true);
        }, 1000);
      } else if (!isEmpty(error)) {
        callBackNotify(error, "error");
      }
    }
  }, [saveKeyFobsLoading, data, error]);

  const validateField = (value, type) => {
    const validators = {
      name: () =>
        value && value.trim().length
          ? { value, error: false, errorText: "Enter name", success: true }
          : { value, error: true, errorText: "Enter name", success: false },
      email: () =>
        value && value.length
          ? EMAIL_REGEX_C360.test(value)
            ? { value, error: false, errorText: "Enter email", success: true }
            : { value, error: true, errorText: "Enter a valid email", success: false }
          : { value, error: true, errorText: "Enter email", success: false },
      phone: () => {
        const phoneRegex = /^([^a-zA-Z])+$/;
        return value && value.length
          ? phoneRegex.test(value)
            ? { value, error: false, errorText: "Enter phone number", success: true }
            : { value, error: true, errorText: "Enter phone number", success: false }
          : { value, error: true, errorText: "Enter phone number", success: false };
      },
      quantity: () => {
        const numVal = parseInt(value);
        return numVal <= keyFobData?.availableQty
          ? { value, error: false, errorText: "Enter quantity", success: true }
          : { value, error: true, errorText: `Enter quantity less than or equal to ${keyFobData?.availableQty}`, success: false };
      },
    };
    return validators[type] ? validators[type]() : { value, error: true, errorText: "Invalid field", success: false };
  };

  const handleFields = (event) => {
    const { name: field, value } = event.target;
    const updatedField = validateField(value, field);
    setFormFields((prevFields) => ({ ...prevFields, [field]: updatedField }));
  };

  useEffect(() => {
    const contactData = cspData?.CSP.length > 0 ? cspData.CSP[0] : cspData?.CASEDetails[0];
    if (contactData) {
      const name = validateField(contactData.CSPContactName, "name");
      const email = validateField(contactData.CSPContactEmail, "email");
      const phone = validateField(contactData.CSPContactPhone, "phone");
      setFormFields((prevFields) => ({ ...prevFields, email, phone, name }));
    }
  }, []);

  const isFieldsValid = useMemo(() => {
    const { name, phone, email, quantity } = formFields;
    return !(name.success && phone.success && email.success && quantity.success && address?.verified);
  }, [formFields, address]);
  const handleToSaveKeyFobs = () => {
    const postObject = {
      vinList: [
        {
          vin: "",
          year: "",
          make: "",
          model: "",
          quantity: 0,
          killSwitch: false,
          emergency: false,
          wheelChair: false,
          fuelType: "",
          weightClass: "",
        },
      ],
      registrationNumber: "",
      accountId: cspData?.AccountID,
      type: "KEYFOB",
      vehicleAssetName: "",
      address1: address?.address1?.value,
      address2: address?.address2?.value,
      city: address?.city?.value,
      country: address.country?.value.description,
      countryCode: address.country?.value.code,
      stateCode: address?.state?.value.erpStateCode,
      zipCode: address.zip.value,
      salesOrgId: cspData?.CSP[0].Opp_Sales_Org,
      timezone: address?.timezone,
      addressByPass: address?.addressByPass,
      locationFound: address?.locationFound,
      locusLocationId: address?.locusLocationId,
      latitude: address?.latitude,
      longitude: address?.longitude,
      locusSuggestedAddress: address?.locusSuggestedAddress,
      customerName: formFields?.name.value,
      customerEmail: formFields?.email.value,
      customerPhone: formFields?.phone.value,
      createdBy: `${cspData?.firstName} ${cspData?.lastName}`,
      sourceApplication: "C360",
      bundleId: selectedKeyFobRow?.BundleId,
      crmRefId: cspData?.CSP?.[0]?.CRM_Ref_ID,
      bundleOrderType: "",
      productPlatform: cspData?.ProductPlatform,
      accountBrand: cspData?.Account_Brand,
      quantity: Number(formFields?.quantity?.value),
      govFlag: cspData?.VZCSegement === "Government",
      kfbVehicleId: selectedKeyFobRow?.vin,
      vehicleSubscriptions: [],
    };

    const products = cspData.Product || [];

    const coreProducts = products.filter(
      (product) =>
        product.BundleId === selectedKeyFobRow?.BundleId &&
        product.featureType === "CORE" &&
        product.AssetType.toLowerCase() === selectedKeyFobRow.AssetType.toLowerCase()
    );

    const subscriptions = {};

    coreProducts.forEach((product) => {
      const subscription = product.SAPProductID;
      const techRefId = product.TechRefId;

      if (!subscriptions[subscription]) {
        subscriptions[subscription] = {
          subscription,
          techRefId,
          vehicleFeatures: [],
        };
      }
      subscriptions[subscription].vehicleFeatures.push({
        materialId: selectedKeyFobRow?.SAPProductID,
        techRefId: selectedKeyFobRow?.TechRefId,
      });
    });
    postObject.vehicleSubscriptions = Object.values(subscriptions);

    saveKeyFobsData(postObject);
  };

  const renderAddress = useMemo(() => {
    return (
      <KeyFobValidateAddress
        showHistoricalAddressDropdown={true}
        validAddress={validAddress}
        address={address}
        setAddress={setAddress}
        setValidAddress={setValidAddress}
        setLoader={setLoader}
        modal
        callBackNotify={callBackNotify}
      />
    );
  }, [address]);

  return (
    <div>
      <CustomModal
        isOpen={open}
        title="Assign to Address"
        description={`${keyFobData?.availableQty} Remaining`}
        closeModal={() => {
          setFormFields(initialFields);
          close(false);
        }}
      >
        <ModalBody css={{ padding: "0 0 0 0" }}>
          <Loader active={isLoading || saveKeyFobsLoading} fullscreen={false} />
          {notification}
          {renderAddress}
          <FlexRow css={{ marginTop: "1rem" }}>
            <div css={{ width: "100%", display: "flex" }}>
              <CustomInput
                type="text"
                label="Contact Name*"
                name="name"
                css={{ marginRight: "6px" }}
                aria-label="Contact Name"
                data-testid="name-field"
                error={formFields?.name?.error || false}
                errorText={formFields?.name?.errorText || ""}
                success={formFields?.name?.success || false}
                value={formFields?.name?.value || ""}
                maxLength={50}
                onChange={handleFields}
                onBlur={handleFields}
              />
              <CustomInput
                type="text"
                label="Phone*"
                aria-label="Phone*"
                name="phone"
                data-testid="phone-field"
                error={formFields?.phone?.error || false}
                errorText={formFields?.phone?.errorText || ""}
                success={formFields?.phone?.success || false}
                value={formFields?.phone?.value || ""}
                onChange={handleFields}
                onBlur={handleFields}
              />
            </div>
          </FlexRow>
          <FlexRow>
            <div css={{ width: "100%", display: "flex" }}>
              <CustomInput
                type="text"
                label="Email*"
                name="email"
                data-testid="email-field"
                css={{ marginRight: "6px" }}
                aria-label="Email*"
                error={formFields?.email?.error || false}
                errorText={formFields?.email?.errorText || ""}
                success={formFields?.email?.success || false}
                value={formFields?.email?.value || ""}
                onChange={handleFields}
                onBlur={handleFields}
              />
              <CustomInput
                type="text"
                label="Quantity*"
                aria-label="Quantity"
                name="quantity"
                data-testid="quantity-field"
                error={formFields?.quantity?.error || false}
                errorText={formFields?.quantity?.errorText || ""}
                success={formFields?.quantity?.success || false}
                value={formFields?.quantity?.value || ""}
                onChange={handleFields}
                onBlur={handleFields}
              />
            </div>
          </FlexRow>
          <FlexRow style={{ justifyContent: "flex-end", width: "100%" }}>
            <CustomSLButton
              secondary
              aria-label="Cancel"
              role="button"
              onClick={() => {
                setFormFields(initialFields);
                close(false);
              }}
            >
              Cancel
            </CustomSLButton>
            <CustomSLButton
              primary
              aria-label="Save"
              role="button"
              style={{ marginRight: "0px" }}
              disabled={isFieldsValid}
              onClick={() => {
                handleToSaveKeyFobs();
              }}
            >
              Save
            </CustomSLButton>
          </FlexRow>
        </ModalBody>
      </CustomModal>
    </div>
  );
};

export default React.memo(AddKeyFobsModal);
